import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useAuth } from "@/auth/AuthProvider";
import {
  cloneBondSubmission,
  withdrawBondSubmission,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { cloneRefusal, withdrawRefusal } from "@/lib/bondLifecycle";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S323 — the submission profile's lifecycle pieces (consuming PMS-S718):
// Withdraw and the withdrawn record with "start a new submission from this
// one"; the status history is the History tab's table (BRK-S427). NOTHING
// HERE IS OPTIMISTIC (NN #7): the withdrawn state renders from the withdraw's
// RESPONSE, and the new draft is the clone's RESPONSE — a failure says so and
// changes nothing on screen.

const PANEL =
  "flex min-w-0 flex-col gap-3 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6";

/**
 * The Withdraw confirm dialog, opened by the submission's action row (its
 * Withdraw Bond is enabled only while PMS says so: `actions.withdraw`, or
 * `canWithdraw` from an older PMS — rule 21, D9). It takes an optional reason;
 * the parent swaps in the order the server answered with, and only then.
 */
export function BondWithdrawDialog({
  order,
  open,
  onClose,
  onWithdrawn,
}: {
  order: BondSubmission;
  open: boolean;
  onClose: () => void;
  onWithdrawn: (fresh: BondSubmission) => void;
}) {
  const { authedRequest } = useAuth();
  const titleId = useId();
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh dialog each time it opens: no stale refusal from the last try.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setError(null);
  }

  async function withdraw() {
    setBusy(true);
    setError(null);
    try {
      const fresh = await withdrawBondSubmission(
        authedRequest,
        order.id,
        reason,
      );
      onClose();
      onWithdrawn(fresh);
    } catch (err) {
      setError(withdrawRefusal(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => (busy ? undefined : onClose())}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id={titleId}>Withdraw {order.number}?</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-3 text-sm text-brk-text">
          <p>
            Withdrawing cancels this request. It can’t be reopened, but you can
            start a new submission from it.
          </p>
          <label htmlFor={reasonId} className="flex flex-col gap-1">
            <span>Reason (optional)</span>
            <textarea
              id={reasonId}
              value={reason}
              rows={3}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
            />
          </label>
          {error && (
            <p role="alert" className="text-brk-danger">
              {error}
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className={buttonClass({ variant: "secondary" })}
        >
          Keep it
        </button>
        <button
          type="button"
          onClick={() => void withdraw()}
          disabled={busy}
          className={buttonClass()}
        >
          {busy ? "Withdrawing…" : "Withdraw submission"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * A WITHDRAWN order: the date and the reason, and the one way on — a new
 * draft prefilled from it (the clone route), resumed at its first incomplete
 * step. The withdrawn order itself is never editable.
 */
export function BondWithdrawnNotice({ order }: { order: BondSubmission }) {
  const { authedRequest } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startAgain() {
    setBusy(true);
    setError(null);
    try {
      const draft = await cloneBondSubmission(authedRequest, order.id);
      navigate(`/surety/submissions/${encodeURIComponent(draft.id)}/continue`);
    } catch (err) {
      setError(cloneRefusal(err));
      setBusy(false);
    }
  }

  return (
    <div role="status" className={PANEL}>
      <p className="text-sm text-brk-text">
        <span className="font-medium">
          Withdrawn on {formatBondDate(order.withdrawnAt ?? "")}.
        </span>{" "}
        It’s kept here for your records and can’t be reopened.
      </p>
      {order.withdrawReason && (
        <p className="break-words text-sm text-brk-dim">
          <span className="text-brk-text">Reason: </span>
          {order.withdrawReason}
        </p>
      )}
      <button
        type="button"
        onClick={() => void startAgain()}
        disabled={busy}
        className={`self-start ${buttonClass()}`}
      >
        {busy ? "Starting…" : "Start a new submission from this one"}
      </button>
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
    </div>
  );
}
